// Define the interface for the analytics service
export interface AnalyticsEvent {
  name: string;
  params?: Record<string, any>;
}

/**
 * Google Analytics service for tracking page views and events
 * across both web and mobile platforms.
 */
class AnalyticsService {
  private measurementId: string = '';
  private isInitialized: boolean = false;

  /**
   * Initialize the analytics service with the Google Analytics measurement ID
   * @param measurementId - The Google Analytics measurement ID (G-XXXXXXXXXX)
   */
  initialize(measurementId: string): void {
    this.measurementId = measurementId;

    this.initializeWeb();

    this.isInitialized = true;
  }

  /**
   * Initialize Google Analytics for web
   */
  private initializeWeb(): void {
    if (typeof window !== 'undefined') {
      // Add Google Analytics script to the page
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
      document.head.appendChild(script);

      // Initialize the dataLayer
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', this.measurementId, {
        send_page_view: false, // We'll track page views manually
      });
    }
  }

  /**
   * Track a page view
   * @param path - The path of the page
   * @param title - The title of the page
   */
  trackPageView(path: string, title?: string): void {
    if (!this.isInitialized) {
      console.warn('Analytics not initialized. Call initialize() first.');
      return;
    }

    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title || document.title,
        page_location: window.location.href,
      });
    }
  }

  /**
   * Track a custom event
   * @param event - The event to track
   */
  trackEvent(event: AnalyticsEvent): void {
    if (!this.isInitialized) {
      console.warn('Analytics not initialized. Call initialize() first.');
      return;
    }

    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', event.name, event.params);
    }
  }
}

// Add type definitions for the global window object
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Export a singleton instance of the service
const analytics = new AnalyticsService();
export default analytics;